import { createApp } from 'vue';
import { createPinia } from 'pinia';
import uiFramework from 'ui-framework';
import { configure } from 'vee-validate';
import '@/validators/customValidators';
import initDirectives from '@/utils/directives';
import initFilters from '@/utils/filters';
import { User } from '@/types/User';
import App from './App.vue';
import router from './router';
import GlobalMixin from './mixins/global';
import { initBugsnag } from './helpers/bugsnag';
import i18n from './locale';
import { getUserFromStorage } from './helpers/auth';
import { useGlobalStore } from './stores/useGlobalStore';

// Suppress duplicate component registration warnings in development
if (process.env.NODE_ENV === 'development') {
    const originalWarn = console.warn;
    console.warn = (...args) => {
        const message = args[0];
        if (typeof message === 'string' && message.includes('has already been registered')) {
            return;
        }
        originalWarn.apply(console, args);
    };
}

const user: null | User = getUserFromStorage();
const pinia = createPinia();

if (process.env.VUE_APP_DEPLOY_TYPE === 'production') {
    initBugsnag(user);
}

configure({
    generateMessage: ctx => {
        const messages: Record<string, string> = {
            required: 'VALIDATION.FIELD.REQUIRED',
            email: 'VALIDATION.FIELD.EMAIL',
            minLength: 'VALIDATION.FIELD.CHARACTERS.MIN',
            maxLength: 'VALIDATION.FIELD.CHARACTERS.MAX',
            length: 'VALIDATION.FIELD.CHARACTERS.EXACT',
            token: 'VALIDATION.FIELD.CHARACTERS.EXACT',
            passwordMatch: 'VALIDATION.FIELD.MATCHED',
        };
        return messages[ctx.rule?.name || ''] || 'VALIDATION.FIELD.REQUIRED';
    },
});

const app = createApp(App);

initFilters(app);
app.mixin(GlobalMixin);

// Register ui-framework components with duplicate registration protection
const registerUIFramework = () => {
    // Check if components are already registered to prevent duplicate registration
    const componentNames = ['FwBurgerIcon', 'FwButton', 'FwInput', 'FwIcon', 'FwMenu'];
    // @ts-ignore-next-line
    const alreadyRegistered = componentNames.some(name => app._component?.components?.[name]);

    if (!alreadyRegistered) {
        try {
            app.use(uiFramework);
        } catch (error) {
            // Suppress duplicate component registration warnings
            if (error instanceof Error && error.message.includes('already been registered')) {
                console.warn('UI Framework components already registered, skipping duplicate registration');
                return;
            }
            throw error;
        }
    } else {
        console.warn('UI Framework components already registered, skipping duplicate registration');
    }
};

registerUIFramework();

initDirectives(app);

app.use(router);
app.use(pinia);
app.use(i18n);

if (user) {
    const globalStore = useGlobalStore();
    globalStore.mutationLoginUser(user);
}

app.mount('#app');
